<template>
  <form-item-facade class="select-item" :name="name" :errors="errors">
    <select-facade
      v-model="model"
      :disabled="disabled"
      :id="name"
      :options="options"
      :placeholder="placeholder"
      :allow-clear="false"
      :show-search="true"
      :loading="isLoading"
      :not-found-content="isLoading ? undefined : null"
      @search="handleSearch"
      @blur="handleBlur"
    />
  </form-item-facade>
</template>

<script setup>
  import { defineModel, onMounted, ref } from 'vue';
  import FormItemFacade from '@/lib/ant-design-vue/components/FormItemFacade.vue';
  import { debounce } from 'lodash-es';
  import SelectFacade from '@/lib/ant-design-vue/components/SelectFacade.vue';

  const model = defineModel();

  const props = defineProps({
    // HTML-поле "name" для элемента формы.
    name: {
      type: [Array, String],
      required: true,
    },

    // Отключен ли выбор из списка
    disabled: {
      type: Boolean,
      default: false,
    },

    // Объект, обрабатывающий ошибки.
    errors: Object,

    // Текст подсказки, что нужно выбрать в списке
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * Асинхронная функция для загрузки элементов из API с поддержкой поиска по
     * атрибуту {@link searchableProperty}
     * @example
     *   :get-items="api.projectExecutor.index"
     */
    getItems: {
      type: Function,
      default: async () => [],
    },

    /**
     * Ключ в ответе API, содержащий объект с данными элементов.
     * Используется для извлечения массива items из структуры ответа.
     * @example
     * // Пример структуры ответа API:
     * {
     *   "payload": {
     *     "executors": {
     *       "items": [
     *         { "id": 1, "name": "Item 1" },
     *         { "id": 2, "name": "Item 2" }
     *       ]
     *     }
     *   }
     * }
     * // В этом случае entity должно быть "executors"
     */
    entity: {
      type: String,
      required: true,
    },

    /**
     * Название свойства, по которому осуществляется поиск на стороне API.
     */
    searchableProperty: {
      type: String,
      default: 'name',
    },

    /**
     * Дополнительные параметры поиска, которые будут передаваться в каждый запрос к API.
     * @example
     * const searchParams = new URLSearchParams({
     *   page: 1,
     *   per-page: 20,
     *   role: 'administrator'
     * });
     */
    defaultSearchParams: {
      type: Object,
      default: () => new URLSearchParams(),
    },

    /**
     * Функция-маппер для преобразования элементов из API в формат опций select.
     * Должна возвращать массив объектов с полями value и label.
     */
    getOptions: {
      type: Function,
      default: () => [],
    },
  });

  const options = ref([]);
  const isLoading = ref(false);

  /**
   * Загружает опции из API при монтировании компонента.
   * Если уже есть выбранное значение, временно отображает плейсхолдер
   * 'загрузка', пока не загрузится список опций.
   */
  onMounted(async () => {
    if (model.value) {
      options.value = [{ value: model.value, label: 'загрузка...' }];
    }

    await getOptions();
  });

  const getOptions = async (name) => {
    try {
      isLoading.value = true;
      const searchParams = props.defaultSearchParams;

      if (name) {
        searchParams.set(props.searchableProperty, name);
      } else {
        searchParams.delete(props.searchableProperty);
      }

      const response = await props.getItems(searchParams);
      options.value = props.getOptions(response[props.entity].items);
    } catch (error) {
      console.log(error);
    } finally {
      isLoading.value = false;
    }
  };

  const handleSearch = debounce(async (name) => await getOptions(name), 300);

  const handleBlur = async () => {
    await getOptions();
  };
</script>
